
// @/services/helia/core/fileOperations.ts

import type { CID } from "multiformats";

import { parseCid } from "@/services/poc/cidUtils";
import { concatUint8Arrays } from "@/services/poc/dataUtils";
import type { CidDownloadLogger } from "@/services/abstract/poc/cidDownloadLogger.interface";
import type { HeliaNode, ProgressCallback, AddFileResult } from "@/types/poc/helia";
import { createLogger, type Logger } from '@/utils/logger';
import { calculateChunkProgress } from '@/utils/downloadProgress';

const log: Logger = createLogger({ namespace: 'six3ro:utils:file-ops' });

/**
 * Compute a hex-encoded SHA-256 hash of a Uint8Array using the Web Crypto API.
 */
async function sha256Hex(data: Uint8Array): Promise<string> {
  const hashBuffer = await crypto.subtle.digest('SHA-256', data.buffer as ArrayBuffer);
  const hashArray = new Uint8Array(hashBuffer);
  return Array.from(hashArray).map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Verify that a DAG written to the local blockstore can be read back intact.
 * Catches malformed DAG structures (e.g. blockstore-fs API mismatches or
 * resource-constrained writes) before the CID is announced to the network.
 *
 * When `expectedHash` is provided, the readback bytes are hashed and compared
 * to pinpoint whether corruption happened during `fs.addBytes()` or earlier.
 *
 * All data is local (IDBBlockstore) so this is millisecond-level overhead.
 */
export async function verifyDagIntegrity(
  heliaNode: HeliaNode,
  cid: CID,
  expectedSize?: number,
  expectedHash?: string
): Promise<{ valid: true; byteCount: number } | { valid: false; error: string }> {
  try {
    const chunks: Uint8Array[] = [];
    let byteCount = 0;
    for await (const chunk of heliaNode.fs.cat(cid)) {
      chunks.push(chunk);
      byteCount += chunk.length;
    }
    if (expectedSize !== undefined && byteCount !== expectedSize) {
      return { valid: false, error: `Size mismatch: readback ${byteCount} bytes vs expected ${expectedSize}` };
    }
    if (expectedHash !== undefined) {
      const readback = concatUint8Arrays(chunks);
      const readbackHash = await sha256Hex(readback);
      if (readbackHash !== expectedHash) {
        return { valid: false, error: `Hash mismatch: input SHA-256 ${expectedHash.substring(0, 16)}... vs readback ${readbackHash.substring(0, 16)}... — fs.addBytes() corrupted the data` };
      }
    }
    return { valid: true, byteCount };
  } catch (error) {
    return { valid: false, error: error instanceof Error ? error.message : String(error) };
  }
}

/**
 * Add a file to Helia
 *
 * @param heliaNode - The Helia node instance
 * @param file - The file to add
 * @param onProgress - Optional progress callback
 * @returns Promise with the CID and other info
 */
export const addFile = async(
  heliaNode: HeliaNode,
  file: File,
  onProgress?: ProgressCallback
): Promise<AddFileResult> => {
  try {
    const { fs } = heliaNode;

    log.info(`[ADD_FILE] converting file to bytes and adding to local helia blockstore: ${file.name}`);
    log.debug(`Size: ${file.size} bytes, Type: ${file.type}`);

    // Convert file to Uint8Array
    const arrayBuffer = await file.arrayBuffer();
    const fileData = new Uint8Array(arrayBuffer);
    log.info(`Converted to Uint8Array: ${fileData.length} bytes`);

    if (onProgress !== undefined) {
      onProgress(fileData.length, fileData.length);
    }

    // Hash input bytes before writing — used to detect fs.addBytes() corruption
    const inputHash = await sha256Hex(fileData);
    log.info(`Input SHA-256: ${inputHash.substring(0, 16)}...`);

    // Use addBytes for raw block storage (no UnixFS wrapping)
    // This creates a simpler CID structure that's easier to retrieve
    log.debug(`Calling fs.addBytes()...`);
    const cid = await fs.addBytes(fileData);
    log.info(`Added file as raw bytes to local blockstore, CID: ${cid.toString()}`);

    // Verify DAG integrity before announcing — catches corrupt writes early
    // Hash comparison pinpoints whether corruption is in fs.addBytes() or earlier
    const verification = await verifyDagIntegrity(heliaNode, cid, fileData.length, inputHash);
    if (!verification.valid) {
      log.error('DAG integrity check failed', { cid: cid.toString(), error: verification.error });
      throw new Error(`File write verification failed: ${verification.error}`);
    }
    log.info(`DAG integrity verified: ${verification.byteCount} bytes, hash matches`);

    // Pin the file to prevent garbage collection
    // pins.add() is an AsyncGenerator — must be consumed to drive the DAG walk
    log.debug(`Pinning CID: ${cid.toString()}`);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- consuming generator
    for await (const _block of heliaNode.helia.pins.add(cid)) { /* consume generator */ }
    log.info(`Pinned CID: ${cid.toString()}`);

    // Log CID details
    let codecName = 'other';
    if (cid.code === 0x55) codecName = 'raw';
    else if (cid.code === 0x70) codecName = 'dag-pb';

    log.debug(`CID Details:`, {
      version: cid.version,
      code: cid.code,
      codecName,
      multihash: {
        code: cid.multihash.code,
        size: cid.multihash.size
      },
      isIdentity: cid.multihash.code === 0x00
    });

    return {
      cid,
      path: file.name,
      size: file.size
    };
  } catch (error) {
    log.error('Error adding file to Helia:', error);
    throw error;
  }
};

// CID codec constants
const RAW_CODEC = 0x55;
const DAG_PB_CODEC = 0x70;

// Chunk logging interval (log every Nth chunk, matching HAP pattern)
const CHUNK_LOG_INTERVAL = 5;

interface ChunkLogParams {
  logger: CidDownloadLogger | undefined;
  cidString: string;
  chunkCount: number;
  bytesLoaded: number;
  startTime: number;
  expectedSize: number | undefined;
}

/**
 * Helper to log chunk progress during file download
 */
function logChunkProgress(params: ChunkLogParams): void {
  const { logger, cidString, chunkCount, bytesLoaded, startTime, expectedSize } = params;
  if (logger === undefined) return;

  const shouldLog = chunkCount === 1 || chunkCount % CHUNK_LOG_INTERVAL === 0;
  if (!shouldLog) return;

  const progress = calculateChunkProgress(bytesLoaded, chunkCount, startTime, expectedSize);
  const step = chunkCount === 1 ? 'fetching' as const : 'chunk-progress' as const;
  logger.log(cidString, step, { chunks: chunkCount, ...progress });
}

/**
 * Options for getFile operation
 */
export interface GetFileOptions {
  onProgress?: ProgressCallback;
  downloadLogger?: CidDownloadLogger;
  expectedSize?: number;
  signal?: AbortSignal;
}

/**
 * Get a file from Helia by CID
 * Handles both raw blocks (codec 0x55) and UnixFS dag-pb content (codec 0x70)
 *
 * @param heliaNode - The Helia node instance
 * @param cid - The CID of the file to retrieve
 * @param optionsOrProgress - Options object or legacy progress callback
 * @returns Promise with the file data as Uint8Array
 */
export const getFile = async(
  heliaNode: HeliaNode,
  cid: string | CID,
  optionsOrProgress?: GetFileOptions | ProgressCallback
): Promise<Uint8Array> => {
  // Support both new options object and legacy progress callback
  const options: GetFileOptions = typeof optionsOrProgress === 'function'
    ? { onProgress: optionsOrProgress }
    : optionsOrProgress ?? {};
  const { onProgress, downloadLogger, expectedSize, signal } = options;
  try {
    const parsedCid = typeof cid === 'string' ? parseCid(cid) : cid;
    log.info(`[GET_FILE] Starting retrieval for CID: ${parsedCid.toString()}`);

    // Log CID properties for debugging
    const codecName = parsedCid.code === RAW_CODEC ? 'raw' : parsedCid.code === DAG_PB_CODEC ? 'dag-pb' : 'other';
    log.debug(`CID Details:`, {
      version: parsedCid.version,
      code: parsedCid.code,
      codecName,
      multihash: {
        code: parsedCid.multihash.code,
        size: parsedCid.multihash.size,
        digest: parsedCid.multihash.digest.length
      },
      isIdentity: parsedCid.multihash.code === 0x00 // Identity hash code
    });

    const { fs, helia } = heliaNode;

    // Check if file exists in blockstore
    log.debug(`Checking blockstore for CID...`);
    const hasBlock = await helia.blockstore.has(parsedCid);
    log.debug(`Blockstore has CID: ${hasBlock}`);

    // Handle raw blocks directly from blockstore (files added with fs.addBytes)
    if (parsedCid.code === RAW_CODEC) {
      log.debug(`CID is raw codec (0x55), attempting direct blockstore read...`);

      try {
        const iterator = helia.blockstore.get(parsedCid);
        const chunks: Uint8Array[] = [];
        for await (const chunk of iterator) {
          chunks.push(chunk);
        }
        const block = chunks.length === 1 ? chunks[0] : concatUint8Arrays(chunks);

        log.info(`Retrieved raw block via blockstore.get(), size: ${block.length} bytes`);
        if (onProgress !== undefined) {
          onProgress(block.length, block.length);
        }
        return block;
      } catch (blockError) {
        log.warn(`blockstore.get() failed, falling back to fs.cat():`, blockError);

        try {
          const catChunks: Uint8Array[] = [];
          let catBytesLoaded = 0;
          let catChunkCount = 0;
          const catStartTime = Date.now();
          const rawCidString = parsedCid.toString();

          downloadLogger?.log(rawCidString, 'requesting', { expectedSize, fallback: true });

          for await (const chunk of fs.cat(parsedCid, { signal })) {
            catChunkCount += 1;
            catChunks.push(chunk);
            catBytesLoaded += chunk.length;

            logChunkProgress({
              logger: downloadLogger,
              cidString: rawCidString,
              chunkCount: catChunkCount,
              bytesLoaded: catBytesLoaded,
              startTime: catStartTime,
              expectedSize
            });
          }
          const catResult = concatUint8Arrays(catChunks);
          log.info(`Retrieved raw block via fs.cat() fallback, size: ${catResult.length} bytes`);

          downloadLogger?.log(rawCidString, 'stored', {
            totalBytes: catResult.length,
            totalChunks: catChunkCount,
            durationMs: Date.now() - catStartTime
          });

          if (onProgress !== undefined) {
            onProgress(catResult.length, catResult.length);
          }
          return catResult;
        } catch (catError) {
          log.error(`Both blockstore.get() and fs.cat() failed for raw block:`, catError);
          downloadLogger?.log(parsedCid.toString(), 'error', {
            error: catError instanceof Error ? catError.message : 'Unknown error',
            fallback: true
          });
          throw blockError;
        }
      }
    }

    // Handle UnixFS content (dag-pb) using fs.cat()
    log.debug(`CID is dag-pb codec (0x70), reading via fs.cat()...`);
    const cidString = parsedCid.toString();
    const chunks: Uint8Array[] = [];
    let bytesLoaded = 0;
    let chunkCount = 0;
    const startTime = Date.now();

    downloadLogger?.log(cidString, 'requesting', { expectedSize });

    try {
      for await (const chunk of fs.cat(parsedCid, { signal })) {
        chunkCount += 1;
        chunks.push(chunk);
        bytesLoaded += chunk.length;
        log.trace(`Received chunk: ${chunk.length} bytes (total: ${bytesLoaded})`);

        // Log chunk progress (matching HAP pattern)
        logChunkProgress({
          logger: downloadLogger,
          cidString,
          chunkCount,
          bytesLoaded,
          startTime,
          expectedSize
        });

        if (onProgress !== undefined) {
          onProgress(bytesLoaded, expectedSize ?? 0);
        }
      }
    } catch (catError) {
      log.error(`fs.cat() failed:`, catError);
      log.error(`Error details:`, {
        message: catError instanceof Error ? catError.message : 'Unknown',
        stack: catError instanceof Error ? catError.stack : 'N/A',
        cidUsed: cidString
      });
      downloadLogger?.log(cidString, 'error', {
        error: catError instanceof Error ? catError.message : 'Unknown error',
        bytesLoaded,
        chunkCount
      });
      throw catError;
    }

    if (chunks.length > 0) {
      const result = concatUint8Arrays(chunks);
      log.info(`Retrieved file, total size: ${result.length} bytes`);
      downloadLogger?.log(cidString, 'stored', {
        totalBytes: result.length,
        totalChunks: chunkCount,
        durationMs: Date.now() - startTime
      });
      return result;
    }

    downloadLogger?.log(cidString, 'error', { error: 'No data retrieved from Helia' });
    throw new Error('No data retrieved from Helia');
  } catch (error: any) {
    log.error('Error getting file from Helia:', error);
    throw new Error(`Failed to retrieve file: ${error.message}`);
  }
};

/**
 * Check if a file exists in the Helia node
 *
 * @param heliaNode - The Helia node instance
 * @param cid - The CID of the file to check
 * @returns Promise resolving to true if the file exists, false otherwise
 */
export const fileExists = async(
  heliaNode: HeliaNode,
  cid: string | CID
): Promise<boolean> => {
  try {
    const parsedCid = typeof cid === 'string' ? parseCid(cid) : cid;
    await heliaNode.fs.stat(parsedCid);
    return true;
  } catch {
    return false;
  }
};

